import { useState } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Add as AddIcon } from "@mui/icons-material";
import { Button } from "@mui/material";
import type { GridSortModel } from "@mui/x-data-grid";
import useAsyncAbortableRequest from "hooks/useAsyncAbortableRequest";
import type { FilterValue } from "hooks/useFilters";
import useFilters from "hooks/useFilters";
import usePagination from "hooks/usePagination";
import useSorting from "hooks/useSorting";
import { LoadablePage } from "shared/LoadablePage";
import FilterLayout from "components/FilterLayout";
import { FilterSectionType } from "components/FilterLayout/FilterSection";
import type { FilterSectionFieldProps } from "components/FilterLayout/FilterSection";
import { PageContent } from "components/PageContent/PageContent";
import PageHeading from "components/PageHeading";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { CreateRateCardDialog } from "areas/rate-cards/components/CreateRateCardDialog";
import { RateCardList } from "areas/rate-cards/components/RateCardList";
import { rateCardRoutes } from "areas/rate-cards/rate-card-routes";
import { getRateCards } from "areas/rate-cards/services/rateCardService";

const headerSections: FilterSectionFieldProps[] = [
  {
    type: FilterSectionType.Text,
    filterKey: "search",
    label: "Search code or label",
  },
];

function CreateRateCardButton({ onCreate }: { onCreate: () => void }) {
  return (
    <Button
      id="create-rate-card-button"
      onClick={onCreate}
      color="secondary"
      variant="outlined"
      startIcon={<AddIcon />}
    >
      Create Rate Card
    </Button>
  );
}

export function RateCardsPage() {
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { page, setPage, pageSize, setPageSize } = usePagination();
  const { filters, setFilter, resetFilter } = useFilters({ filterMap: {} });
  const { sortModel, setSortModel } = useSorting();

  // Changing the search changes the result set, so a page kept from the previous
  // set could be past the end of the new one — an empty grid with no explanation.
  const handleFilterChange = (key: string, value: FilterValue) => {
    setPage(0);
    setFilter(key, value);
  };

  const handleResetFilter = () => {
    setPage(0);
    resetFilter();
  };

  // Re-sorting reorders the whole result set, so a page held from the old order shows rows nobody
  // asked for — the same hazard as changing a filter.
  const handleSortModelChange = (model: GridSortModel) => {
    setPage(0);
    setSortModel(model);
  };

  const {
    data = { items: [], totalItems: 0 },
    loading,
    error,
  } = useAsyncAbortableRequest(getRateCards, {
    search: typeof filters.search === "string" ? filters.search : undefined,
    page: page + 1, // Convert 0-based to 1-based page number
    pageSize,
    sortFieldName: sortModel[0]?.field,
    sortDirection: sortModel[0]?.sort === "asc" ? "Asc" : sortModel[0]?.sort === "desc" ? "Desc" : undefined,
  });

  return (
    <>
      <PageHeading
        headingText="Rate Cards"
        actionItems={[
          {
            policy: RbacPolicies.RateCards.ReadWrite,
            component: <CreateRateCardButton onCreate={() => setIsCreateDialogOpen(true)} />,
          },
        ]}
      />
      <PageContent>
        <FilterLayout
          headerFilters={headerSections}
          filterSections={[]}
          filter={filters}
          defaultFilter={{}}
          onFilterChange={handleFilterChange}
          onResetFilter={handleResetFilter}
        >
          <LoadablePage error={error}>
            <RateCardList
              rateCards={data.items}
              totalCount={data.totalItems}
              loading={loading}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
            />
          </LoadablePage>
        </FilterLayout>
        <CreateRateCardDialog
          open={isCreateDialogOpen}
          mode={{ kind: "shared" }}
          onCancel={() => setIsCreateDialogOpen(false)}
          onCreated={(rateCard) => {
            setIsCreateDialogOpen(false);
            navigate(rateCardRoutes.detail(rateCard.code).root);
          }}
        />
      </PageContent>
    </>
  );
}
